import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { ServiceFormTemplate } from '@/widgets/ServiceFormTemplate';

import { InvoiceStatus } from '@/shared/types';

import { useAsyncDataLoader } from '@/hooks/useAsyncDataLoader';
import { useFormDraftPlainTools } from '@/hooks/useFormDraftPlainTools';
import { usePaymentShortPolling } from '@/hooks/usePaymentShortPolling';

import { getSteps } from '@/modules/iac/pages/services/ExtendedCriminalRecord/ui/steps/steps';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { DeliveryType } from '@/lib/consts';
import { queryClient } from '@/lib/queryClient';

import { queryKeys } from '@/api/queryKeys';
import {
  DataResponse,
  FormDataType,
  ResponseArtifact,
  UserRequest,
  UserRequestDraft,
  UserService,
  UserServicePaymentType,
} from '@/api/types';

import api from '@/api';

import { ExtractFormType, SERVICE_CODE, STEP_NUMBER_SIGN_UP, STEP_NUMBER_TO_PAY } from './lib/constants';
import { generatePayload } from './lib/generatePayload';

import './ExtendedCriminalRecordExtract.scss';

export const ExtendedCriminalRecordExtract: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const currentService = getServiceByCode(SERVICE_CODE) as UserService;
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const [extractFormType, setExtractFormType] = useState<ExtractFormType>(ExtractFormType.UNSET);
  const [forcePaperPaymentFlow, setForcePaperPaymentFlow] = useState<boolean>(false);
  const [pendingPaymentStepToOpen, setPendingPaymentStepToOpen] = useState<Nullable<number>>(null);
  const [willBePaidLater, setWillBePaidLater] = useState<boolean>(false);
  const [deliveryType, setDeliveryType] = useState<DeliveryType | null>(null);
  const [apostilleRequired, setApostilleRequired] = useState<boolean>(false);
  const { state: locationState } = useLocation();
  const isContinuePaymentFlow: boolean = !!locationState?.continuePayment || !!locationState?.continueGercPayment;

  const {
    isLoading: isLoadingDraft,
    onDraftContinue,
    onDraftSave,
    onDraftCreate,
    onDraftDelete,
    onPaymentContinue,
    currentRequestId,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => {
      setExtractFormType((data?.extractFormType as ExtractFormType) ?? ExtractFormType.UNSET);
      setDefaultValues(data ?? {});
    },
    stepNumberToPay: STEP_NUMBER_TO_PAY,
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
  });

  useEffect(() => {
    if (isContinuePaymentFlow && !!locationState?.request_id) {
      setExtractFormType(ExtractFormType.PAPER);
      onPaymentContinue(locationState?.request_id);
    }
  }, []);

  const { load: generatePDF, isLoading: isLoadingPdf } = useAsyncDataLoader(
    async (...args: Parameters<typeof api.services.generatePdf>) => {
      const result = await api.services.generatePdf(...args);
      serviceFileManager.setAttachment(result);
      return result;
    },
  );

  const [redirectServiceInfo, setRedirectServiceInfo] = useState<{
    link: string;
    request_id: number;
  }>();

  const { mutateAsync: submitForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: ({
      code,
      payload,
      allowToProfileUpdate,
    }: {
      code: Parameters<typeof api.services.submit>[0];
      payload: Parameters<typeof api.services.submit>[1];
      allowToProfileUpdate?: string;
    }) => api.services.submit(code, payload, allowToProfileUpdate),
  });
  const [currentUserRequest, setCurrentUserRequest] = useState<Nullable<UserRequest>>(null);
  const { isShortPollingSuccessed, isShortPollingInProgress, isShortPollingFailed, startPaymentShortPolling } =
    usePaymentShortPolling(setCurrentUserRequest);

  const onGercPaymentContinue = async (userRequestId: UserRequest['id']): Promise<number> => {
    if (sessionStorage.getItem('redirectServiceInfo')) {
      setRedirectServiceInfo(JSON.parse(sessionStorage.getItem('redirectServiceInfo') as string));
      sessionStorage.removeItem('redirectServiceInfo');
    }

    startPaymentShortPolling(userRequestId);

    return onPaymentContinue(userRequestId);
  };

  const handleDraftCreate = useCallback(
    async (
      saveAfterCreate: { stateForm: FormDataType },
      specificStep: (step: number) => void,
    ): Promise<Nullable<string>> => {
      const draftExtractFormType =
        (saveAfterCreate.stateForm.extractFormType as Nullable<ExtractFormType>) ?? ExtractFormType.UNSET;
      const totalSteps = getSteps(draftExtractFormType, true).length;
      const handleSpecificStep = (step: number): void => {
        if (step === STEP_NUMBER_TO_PAY && draftExtractFormType === ExtractFormType.ELECTRONIC) {
          setExtractFormType(ExtractFormType.PAPER);
          setForcePaperPaymentFlow(true);
          setPendingPaymentStepToOpen(step);
          return;
        }

        specificStep(step);
      };

      return onDraftCreate(
        {
          id: currentService.id,
          name: currentService.name,
          code: SERVICE_CODE,
          totalSteps,
          lastStepFilled: 1,
          currentStep: 2,
        },
        handleSpecificStep,
        saveAfterCreate,
      );
    },
    [onDraftCreate, currentService.id, currentService.name],
  );

  const getFreshHasActiveRequests = useCallback(async (): Promise<boolean> => {
    const hasActiveRequestQueryKey = queryKeys.services.hasActiveRequest(SERVICE_CODE);

    await queryClient.invalidateQueries({ queryKey: hasActiveRequestQueryKey });

    const data = await queryClient.fetchQuery({
      queryKey: hasActiveRequestQueryKey,
      queryFn: (): Promise<DataResponse<Record<'answer', boolean>>> => api.services.hasActiveRequest(SERVICE_CODE),
      retry: false,
    });

    return data?.data?.answer;
  }, []);

  const handleDraftContinue = useCallback(
    async (request_id: UserRequest['id']): Promise<number> => {
      const hasActiveRequests = await getFreshHasActiveRequests();

      return hasActiveRequests ? 0 : onDraftContinue(request_id);
    },
    [onDraftContinue, getFreshHasActiveRequests],
  );

  const handleBeforeSignupSubmit = useCallback(
    async (specificStep: (step: number) => void): Promise<boolean | void> => {
      const hasActiveRequests = await getFreshHasActiveRequests();

      if (hasActiveRequests) {
        if (currentRequestId) {
          await onDraftDelete(currentRequestId);
        }

        setExtractFormType(ExtractFormType.UNSET);
        specificStep(1);

        return false;
      }
    },
    [onDraftDelete, currentRequestId, getFreshHasActiveRequests],
  );

  const handleGeneratePDF = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { commonPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
      });

      if (formData.extractFormType === ExtractFormType.ELECTRONIC) {
        const mockAttachment: ResponseArtifact = {
          fileId: null,
          fileName: 'electronic-extract.pdf',
          file: new Blob([new Uint8Array(0)], { type: 'application/pdf' }),
        };

        serviceFileManager.setAttachment(mockAttachment);
        return Promise.resolve();
      } else {
        await generatePDF(SERVICE_CODE, commonPayload);
      }
    },
    [currentRequestId, currentService, generatePDF],
  );

  const handleSubmitForm = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { signedPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
        serviceFileManager,
      });

      await submitForm({
        code: SERVICE_CODE,
        payload: signedPayload,
        allowToProfileUpdate: String(formData.allowProfileUpdate),
      }).catch((error) => console.error(error));

      if (formData.extractFormType === ExtractFormType.PAPER) {
        serviceFileManager.download();
      }

      serviceFileManager.clear();
    },
    [currentRequestId, currentService, serviceFileManager, submitForm],
  );

  const handleExtractFormTypeSelect = (extractForm: ExtractFormType): void => {
    setExtractFormType(extractForm);
  };

  const handleDeliveryTypeChange = (value: DeliveryType): void => {
    setDeliveryType(value);
  };

  const handleApostilleRequiredChange = (value: boolean): void => {
    setApostilleRequired(value);
  };

  const handleWillBePaidLaterClick = (): void => {
    setWillBePaidLater(true);
  };

  const handlePaymentStepOpened = useCallback((): void => {
    setPendingPaymentStepToOpen(null);
  }, []);

  const isLoading = isLoadingDraft || isLoadingPdf || isLoadingSubmit;
  const showPaymentStep: boolean =
    extractFormType === ExtractFormType.PAPER && (apostilleRequired || deliveryType !== DeliveryType.PERSONAL);
  const isPaperPaymentFlow: boolean = (isContinuePaymentFlow && !!locationState?.request_id) || forcePaperPaymentFlow;
  const steps = isPaperPaymentFlow ? getSteps(ExtractFormType.PAPER, true) : getSteps(extractFormType, showPaymentStep);
  const selectedExtractFormType = isPaperPaymentFlow ? ExtractFormType.PAPER : extractFormType;

  return (
    <ServiceFormTemplate
      className="extended-criminal-record-extract"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={steps}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onDraftContinue={handleDraftContinue}
      onDraftSave={onDraftSave}
      onGercPaymentContinue={onGercPaymentContinue}
      onPaymentContinue={onPaymentContinue}
      startCountFromZero
      hideFeedbackForm={extractFormType === ExtractFormType.UNSET}
      stepProps={{
        serviceCode: SERVICE_CODE,
        onDraftDelete: onDraftDelete,
        onDraftContinue: onDraftContinue,
        onDraftCreate: handleDraftCreate,
        handleGeneratePDF,
        isLoading,
        handleDeliveryTypeChange,
        handleApostilleRequiredChange,
        willBePaidLater,
        handleWillBePaidLaterClick,
        onExtractFormTypeSelect: handleExtractFormTypeSelect,
        selectedExtractFormType: selectedExtractFormType,
        paymentStepToOpen: pendingPaymentStepToOpen,
        onPaymentStepOpened: handlePaymentStepOpened,
        allowPersonalDelivery: true,
        serviceFileManager,
        submitForm: handleSubmitForm,
        paymentShortPolling: { isShortPollingInProgress },
        finishedUserRequestStatus: isShortPollingSuccessed || isShortPollingFailed,
        userRequestId: currentRequestId ?? currentUserRequest?.id ?? '',
        allowedPaymentType: currentService.paymentType ?? UserServicePaymentType.WITHOUT_PAYMENT,
        onPaymentContinue,
        invoiceStatus: currentUserRequest?.invoice?.status ?? InvoiceStatus.SUCCESS,
        beforeSignupSubmit: handleBeforeSignupSubmit,
        beforeRedirectToGerc: () => {
          if (redirectServiceInfo) {
            sessionStorage.setItem('redirectServiceInfo', JSON.stringify(redirectServiceInfo));
          }
        },
      }}
    />
  );
};
